import { useEffect, useState, useCallback } from "react";
import { api, extractError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Plus, Pencil, Power } from "lucide-react";
import { toast } from "sonner";

function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", active: true });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/categories", { params: { active_only: false } }); setItems(data); }
    catch (e) { setError(extractError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: "", active: true }); setFormError(""); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, active: it.active }); setFormError(""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true); setFormError("");
    try {
      if (editing) { await api.patch(`/categories/${editing.id}`, form); toast.success("Category updated"); }
      else { await api.post("/categories", form); toast.success("Category created"); }
      setOpen(false); load();
    } catch (e) { setFormError(extractError(e)); }
    finally { setSubmitting(false); }
  };

  const deactivate = async (it) => {
    if (!window.confirm(`Deactivate ${it.name}?`)) return;
    try { await api.delete(`/categories/${it.id}`); toast.success("Category deactivated"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div data-testid="categories-admin">
      <PageHeader
        title="Categories"
        subtitle="Used to classify expenses (Fuel, Parts, Meals, etc.)."
        action={
          <Button onClick={openCreate} data-testid="create-category-btn" className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
            <Plus className="mr-1 h-4 w-4" /> New category
          </Button>
        }
      />
      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="rounded-md border border-slate-200 bg-white">
        <Table data-testid="categories-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} className="text-center text-slate-500">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center text-slate-500">No categories</TableCell></TableRow>
            ) : (
              items.map((it) => (
                <TableRow key={it.id} data-testid={`category-row-${it.name}`}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell>
                    {it.active
                      ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Active</Badge>
                      : <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-category-${it.name}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {it.active && (
                      <Button variant="ghost" size="sm" onClick={() => deactivate(it)} data-testid={`deactivate-category-${it.name}`}>
                        <Power className="h-4 w-4 text-rose-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
              <DialogDescription>Categories help group expenses for reporting.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="cat-name">Name</Label>
              <Input id="cat-name" required value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="category-name-input" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div><Label>Active</Label><p className="text-xs text-slate-500">Hidden from workers if inactive.</p></div>
              <Switch checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
                data-testid="category-active-switch" />
            </div>
            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="category-submit-btn"
                className="bg-[#0F1E3D] text-white hover:bg-[#0F1E3D]/90">
                {submitting ? "Saving…" : editing ? "Save changes" : "Create category"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
